/**
 * Domínio: UI de Contato.
 *
 * Ações sobre o contato e o painel lateral (drawer) da conversa.
 * Acesse via `contacts.events.EDIT`, `contacts.slots.SIDEBAR`.
 *
 * Slots num registro só (`contactsSlots`); `slots`/`slotContext` derivados.
 */
import type { InterceptContextBase } from "@/ui/types";
import { defineSlotRegistry, legacySlotContextOf, slotNamesOf, t, type SlotContextMapOf } from "@/ui/slotSchema";

/** Drawer do contato — o ticket só vem quando o drawer foi aberto de uma conversa. */
const contactDrawer = { contactId: t.number, ticketId: t.optional(t.number) } as const;

export const contactsSlots = defineSlotRegistry({
  ACTIONS: {
    name: "contact-actions",
    description: "Botões do drawer do contato, ao lado de editar/bloquear.",
    context: contactDrawer,
  },
  SIDEBAR: {
    name: "contact-sidebar",
    description: "Painel abaixo dos dados do contato, na aba principal do drawer.",
    context: contactDrawer,
  },
});

export const contacts = {
  /** Eventos de pipeline (intercept before/on/after). */
  events: {
    EDIT: "edit-contact",
    BLOCK: "block-contact",
    SAVE_NOTE: "save-ticket-note",
    CHANGE_KANBAN: "change-kanban-stage",
  },
  /** Slots de render — derivado de `contactsSlots`. */
  slots: slotNamesOf(contactsSlots),
  hooks: {},
  /** @deprecated Formato antigo, derivado do registro. Use `contactsSlots`. */
  slotContext: legacySlotContextOf(contactsSlots),
  registry: contactsSlots,
} as const;

export type ContactsSlotContextMap = SlotContextMapOf<typeof contactsSlots>;
/** contact-actions / contact-sidebar. */
export type ContactSlotContext = ContactsSlotContextMap["contact-actions"];

/** Contato como o frontend o conhece (subconjunto estável). */
export interface UIContact {
  id: number;
  name?: string;
  number?: string;
  [k: string]: any;
}

/** edit-contact: abertura/salvamento de edição do contato. */
export interface EditContactContext extends InterceptContextBase {
  contact: UIContact;
}

/** block-contact: bloqueio do contato. */
export interface BlockContactContext extends InterceptContextBase {
  contact: UIContact;
}

/** save-ticket-note: salvar observação do ticket. */
export interface SaveTicketNoteContext extends InterceptContextBase {
  ticketId: number;
  contactId?: number;
  note: string;
}

/** change-kanban-stage: mudar etapa Kanban do ticket. */
export interface ChangeKanbanStageContext extends InterceptContextBase {
  ticketId: number;
  contactId?: number;
  stageId?: number | string;
}
